import fabric from '@/lib/fabric';
import { isObjectLocked } from '@/lib/layerOps';

export type AlignMode = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';

function getArtboardRect(canvas: fabric.Canvas) {
  return {
    left: 0,
    top: 0,
    width: canvas.getWidth(),
    height: canvas.getHeight(),
  };
}

/**
 * Align active object(s). Single object → artboard.
 * Multi-select → align within the selection bounding box.
 */
export function alignSelection(
  canvas: fabric.Canvas,
  mode: AlignMode,
  relativeTo: 'auto' | 'artboard' | 'selection' = 'auto'
): boolean {
  const objects = canvas.getActiveObjects();
  if (!objects.length) return false;

  const useArtboard =
    relativeTo === 'artboard' ||
    (relativeTo === 'auto' && objects.length === 1);

  let ref: { left: number; top: number; width: number; height: number };

  if (useArtboard) {
    ref = getArtboardRect(canvas);
  } else {
    const active = canvas.getActiveObject();
    if (!active) return false;
    const b = active.getBoundingRect(true);
    ref = { left: b.left, top: b.top, width: b.width, height: b.height };
  }

  objects.forEach((obj) => {
    const b = obj.getBoundingRect(true);
    let dx = 0;
    let dy = 0;

    switch (mode) {
      case 'left':
        dx = ref.left - b.left;
        break;
      case 'center':
        dx = ref.left + ref.width / 2 - (b.left + b.width / 2);
        break;
      case 'right':
        dx = ref.left + ref.width - (b.left + b.width);
        break;
      case 'top':
        dy = ref.top - b.top;
        break;
      case 'middle':
        dy = ref.top + ref.height / 2 - (b.top + b.height / 2);
        break;
      case 'bottom':
        dy = ref.top + ref.height - (b.top + b.height);
        break;
    }

    obj.set({
      left: (obj.left || 0) + dx,
      top: (obj.top || 0) + dy,
    });
    obj.setCoords();
  });

  canvas.requestRenderAll();
  return true;
}

export type DistributeMode = 'horizontal' | 'vertical';

/**
 * Space active objects evenly (gap-based) along an axis.
 * Requires 2+ selected objects.
 */
export function distributeSelection(
  canvas: fabric.Canvas,
  mode: DistributeMode
): boolean {
  const objects = canvas.getActiveObjects();
  if (objects.length < 2) return false;

  const items = objects.map((obj) => {
    const b = obj.getBoundingRect(true);
    return { obj, b };
  });

  if (mode === 'horizontal') {
    items.sort((a, b) => a.b.left - b.b.left);
    const minL = items[0].b.left;
    const maxR = Math.max(...items.map((i) => i.b.left + i.b.width));
    const totalW = items.reduce((sum, i) => sum + i.b.width, 0);
    const gaps = items.length - 1;
    const gap = gaps > 0 ? (maxR - minL - totalW) / gaps : 0;
    let cursor = minL;
    items.forEach((item) => {
      const dx = cursor - item.b.left;
      item.obj.set({ left: (item.obj.left || 0) + dx });
      item.obj.setCoords();
      cursor += item.b.width + gap;
    });
  } else {
    items.sort((a, b) => a.b.top - b.b.top);
    const minT = items[0].b.top;
    const maxB = Math.max(...items.map((i) => i.b.top + i.b.height));
    const totalH = items.reduce((sum, i) => sum + i.b.height, 0);
    const gaps = items.length - 1;
    const gap = gaps > 0 ? (maxB - minT - totalH) / gaps : 0;
    let cursor = minT;
    items.forEach((item) => {
      const dy = cursor - item.b.top;
      item.obj.set({ top: (item.obj.top || 0) + dy });
      item.obj.setCoords();
      cursor += item.b.height + gap;
    });
  }

  const active = canvas.getActiveObject();
  if (active) active.setCoords();
  canvas.requestRenderAll();
  return true;
}

export function groupSelection(canvas: fabric.Canvas): boolean {
  const active = canvas.getActiveObject();
  if (!active || active.type !== 'activeSelection') return false;

  (active as fabric.ActiveSelection).toGroup();
  canvas.requestRenderAll();
  return true;
}

export function ungroupSelection(canvas: fabric.Canvas): boolean {
  const active = canvas.getActiveObject();
  if (!active || active.type !== 'group') return false;

  (active as fabric.Group).toActiveSelection();
  canvas.requestRenderAll();
  return true;
}

/** Hit-test topmost object under a canvas-space point (bounds fallback). */
export function findObjectAtPoint(
  canvas: fabric.Canvas,
  point: { x: number; y: number }
): fabric.Object | null {
  const fabricPoint = new fabric.Point(point.x, point.y);
  const objects = canvas.getObjects();

  for (let i = objects.length - 1; i >= 0; i -= 1) {
    const obj = objects[i];
    if (!obj || obj.visible === false) continue;

    try {
      if (obj.containsPoint(fabricPoint, undefined, true)) {
        return obj;
      }
    } catch {
      const b = obj.getBoundingRect(true);
      if (
        point.x >= b.left &&
        point.x <= b.left + b.width &&
        point.y >= b.top &&
        point.y <= b.top + b.height
      ) {
        return obj;
      }
    }
  }

  return null;
}

export function eraseObjectAtPoint(
  canvas: fabric.Canvas,
  point: { x: number; y: number }
): boolean {
  const target = findObjectAtPoint(canvas, point);
  if (!target || isObjectLocked(target)) return false;

  if (target.type === 'activeSelection') {
    const sel = target as fabric.ActiveSelection;
    const members = sel.getObjects().slice();
    canvas.discardActiveObject();
    members.forEach((obj) => canvas.remove(obj));
  } else {
    canvas.remove(target);
  }

  canvas.requestRenderAll();
  return true;
}
